import React, { forwardRef } from 'react';
import Button, { ButtonProps } from '@athena/spark/Button';
import IconButton, { IconButtonProps } from '@athena/spark/IconButton';
import { useAmplitude } from 'src/lib/amplitude';
import useIsMobile from '../../../lib/hooks/useIsMobile';
import { getPlatformType } from '../../../components/utils/Helper';

type AmplitudeProps = {
  action: string;
  object?: string;
  pageName?: string; //Defaults to whatever we infer from the route, but can be overridden.
  pageNameInProps?: boolean; // If set (to true), we'll pass Common::PageName as a prop instead of appending _PageName to the event name
  eventProperties?: Record<string, any>; // not the pageName
};

type otherProps = {
  showLabelHideTooltip?: boolean;
};

export type AmpButtonProps = AmplitudeProps & otherProps & ButtonProps;
export type AmpIconButtonProps = AmplitudeProps & otherProps & IconButtonProps;

// Examples are THWebApp::SwitchLangCTA or THWebApp_Welcome::Next
// export const getEventName = (appName: string, props: AmpButtonProps): string => {
//   return appName + (props.pageName && !props.pageNameAsEventProperty ? '_' + props.pageName : '') + '::' + props.action;
// };

// STOP! You'd better really know what you are doing if you are trying to emulate a button outside of this file
// There is one good use case : Spark Modals / Lightboxes need to log for the native onClose functionality (a 'X' button in the top right corner)
// Since this use case should not be involving setting the pageName as an event property, it has been disabled here. If needed later, copy from logClick() above
// Since this needs to be invoked from a callback, it cannot be a hook. Since it's not a hook, it cannot use useAmplitudeRouteAwareness(), sadly

// Adds an Amplitude tracking event to a basic Spark Button
export const AmplitudeButton = forwardRef<HTMLButtonElement, AmpButtonProps>((props, ref) => {
  const { action, object, pageName, pageNameInProps, eventProperties, lang, ...buttonProps } = props;
  const { ampTrackEvent } = useAmplitude(pageName);

  const handleClick = (props: AmpButtonProps, e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick && props.onClick(e);
    ampTrackEvent({
      action: props.action,
      object: props.object,
      pageNameInProps: props.pageNameInProps,
      data: {
        ...props.eventProperties,
      },
    });
  };

  return <Button ref={ref} {...buttonProps} onClick={(e) => handleClick({ ...props }, e)} lang={lang} />;
});

AmplitudeButton.displayName = 'AmplitudeButton';

// Adds an Amplitude tracking event to a basic Spark Icon-Button
export const AmplitudeIconButton = forwardRef<HTMLButtonElement, AmpIconButtonProps>((props, ref) => {
  const { action, object, pageName, pageNameInProps, eventProperties, showLabelHideTooltip, ...iconButtonProps } =
    props;
  const isMobile = useIsMobile();
  const platformType = getPlatformType();
  const { ampTrackEvent } = useAmplitude(pageName);

  const handleClick = (props: AmpButtonProps, e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick && props.onClick(e);

    ampTrackEvent({
      action: props.action,
      object: props.object,
      pageNameInProps: props.pageNameInProps,
      data: {
        ...props.eventProperties,
      },
    });
  };

  return (
    <IconButton
      ref={ref}
      {...iconButtonProps}
      onClick={(e) => handleClick({ ...props } as AmpButtonProps, e)}
      hideTooltip={isMobile || showLabelHideTooltip || platformType === 'tablet'}
    />
  );
});

AmplitudeIconButton.displayName = 'AmplitudeIconButton';
